import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm, useFormState } from 'react-hook-form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { AddCommandsDialog, InternalIdInput, JsonEditor } from '@library'
import { ActionsConfigTab } from '@mobileComponents/EventFlow'
import { MobileConfiguratorLayout } from '@mobileComponents/MobileConfiguratorLayout'
import { SaveDraftDialog } from '@mobileComponents/SaveDraftDialog'
import { TabPanel } from '@mobileComponents/TabPanel'
import { VariablesConfigTab } from '@mobileComponents/Variables'
import {
  buildListItemScriptFieldsInfo,
  buildListItemScriptVariables,
  DEFAULT_MOBILE_LIST_ITEM,
} from '@mobileConstants'
import {
  convertSimpleToAdvanced,
  extractServerErrorMessage,
  normalizeListItemDisplayRules,
  validateMobileListItem,
} from '@mobileHelpers'
import { useDraft, useMobileGlobalVariables, useMobileListItems } from '@mobileHooks'
import { MobileListItemPreview } from '@mobilePreviews'
import {
  DEFAULT_LIST_ITEM_PREVIEW_SETTINGS,
  GETCommand,
  ListItemPreviewSettings,
  MobileListItem,
  PreviewVariablePackage,
  PreviewVariableValues,
  Variable,
} from '@mobileTypes'
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'

import { useExecuteCommandMutation } from '@redux/api/command.api'

import { getCommandRequestData } from '@helpers'
import { ROUTES } from '@constants'
import { CommandParameterDTO } from '@types'

import { AdvancedConfigTab } from './components/AdvancedConfigTab'
import { ConfigModeSwitchDialog } from './components/ConfigModeSwitchDialog'
import { DataSourceConfig } from './components/DataSourceConfig'
import { FabConfigSection } from './components/FabConfigSection'
import { IconConfigSection } from './components/IconConfigSection'
import { PrefilledVariablesConfig } from './components/PrefilledVariablesConfig'
import { RowsConfigTab } from './components/RowsConfigTab'

interface PreviewCommandResult {
  key: string
  command: GETCommand
  rawData: any
  records: any[]
}

const COMMAND_KEY_PREFIX = 'command'
const FILTER_PARAM_TITLE = 'filter'
const PREVIEW_SETTINGS_STORAGE_PREFIX = 'mobile-list-item-preview-settings'
const LOCAL_VARIABLE_PACKAGES_STORAGE_PREFIX = 'mobile-list-item-preview-local-variable-packages'
const GLOBAL_VARIABLE_PACKAGES_STORAGE_KEY = 'mobile-list-item-preview-global-variable-packages'

const getCommandKey = (index: number) => `${COMMAND_KEY_PREFIX}${index + 1}`

const getScopedStorageKey = (prefix: string, configKey: string) => `${prefix}:${configKey}`

const readLocalStorageValue = <T,>(key: string, fallback: T): T => {
  try {
    const value = window.localStorage.getItem(key)

    return value ? (JSON.parse(value) as T) : fallback
  } catch {
    return fallback
  }
}

const writeLocalStorageValue = (key: string, value: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Preview settings are best-effort developer tooling.
  }
}

const loadPreviewSettings = (configKey: string): ListItemPreviewSettings => ({
  ...DEFAULT_LIST_ITEM_PREVIEW_SETTINGS,
  ...readLocalStorageValue<ListItemPreviewSettings>(
    getScopedStorageKey(PREVIEW_SETTINGS_STORAGE_PREFIX, configKey),
    DEFAULT_LIST_ITEM_PREVIEW_SETTINGS
  ),
})

const loadLocalVariablePackages = (configKey: string): PreviewVariablePackage[] =>
  readLocalStorageValue<PreviewVariablePackage[]>(
    getScopedStorageKey(LOCAL_VARIABLE_PACKAGES_STORAGE_PREFIX, configKey),
    []
  )

const loadGlobalVariablePackages = (): PreviewVariablePackage[] =>
  readLocalStorageValue<PreviewVariablePackage[]>(GLOBAL_VARIABLE_PACKAGES_STORAGE_KEY, [])

const buildVariableDefaultValues = (variables: Variable[] = []): Record<string, any> => {
  return variables.reduce((acc, variable) => {
    acc[variable.internalId] = variable.defaultValue ?? ''

    return acc
  }, {} as Record<string, any>)
}

const getPreviewCommandParameters = (
  parameters: CommandParameterDTO[] | null | undefined,
  skipFilterGetData: boolean
): CommandParameterDTO[] => {
  const list = parameters ?? []

  if (!skipFilterGetData) {
    return list
  }

  return list.filter(param => param.title !== FILTER_PARAM_TITLE)
}

const ensureArray = (value: any): any[] => {
  if (value === undefined || value === null) {
    return []
  }

  return Array.isArray(value) ? value : [value]
}

const normalizeCommandRecords = (result: any): any[] => {
  if (result === undefined || result === null) {
    return []
  }

  if (Array.isArray(result)) {
    if (result.length > 0 && typeof result[0] === 'object' && result[0] !== null) {
      return result.map(entry =>
        entry && typeof entry === 'object' && 'object' in entry ? entry.object ?? entry : entry
      )
    }

    return result
  }

  if (typeof result === 'object') {
    if (Array.isArray(result.items)) {
      return result.items
    }

    if (Array.isArray(result.data)) {
      return result.data
    }

    if (Array.isArray(result.rows)) {
      return result.rows
    }

    if ('object' in result && result.object) {
      return ensureArray(result.object)
    }
  }

  return ensureArray(result)
}

const buildSrcObj = (results: PreviewCommandResult[]): Record<string, any[]> => {
  return results.reduce((acc, entry) => {
    acc[entry.key] = entry.records

    if (entry.command.objectCode) {
      acc[entry.command.objectCode] = entry.records
    }

    return acc
  }, {} as Record<string, any[]>)
}

const evaluateUserCode = (code: string | undefined, context: Record<string, any>) => {
  const trimmed = (code || '').trim()
  if (!trimmed) {
    return undefined
  }

  const hasReturn = /return\b/.test(trimmed)
  const executable = hasReturn ? trimmed : `return (${trimmed});`

  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(...Object.keys(context), executable)

    return fn(...Object.values(context))
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }

    throw new Error(String(error))
  }
}

const extractErrorMessage = (error: unknown): string => {
  if (!error) {
    return 'Неизвестная ошибка при выполнении команды'
  }

  if (typeof error === 'string') {
    return error
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'object') {
    const errObj = error as Record<string, any>

    if (errObj.data) {
      const data = errObj.data
      if (typeof data === 'string') {
        return data
      }

      if (data && typeof data === 'object') {
        return data.message || data.error || data.details || JSON.stringify(data)
      }
    }

    if (typeof errObj.message === 'string') {
      return errObj.message
    }
  }

  return 'Не удалось выполнить команду'
}

const derivePreviewItems = (
  listItem: MobileListItem,
  results: PreviewCommandResult[],
  previewVariableValues: PreviewVariableValues
): any[] => {
  if (!results.length) {
    return []
  }

  const dataSource = listItem?.dataSource
  const srcObj = buildSrcObj(results)

  if (dataSource?.bindType === 'command') {
    if (dataSource.commandKey) {
      const matched = results.find(result => result.key === dataSource.commandKey)
      if (matched) {
        return matched.records
      }
    }

    return results[0].records
  }

  if (dataSource?.bindType === 'js' && dataSource.jsCode) {
    const output = evaluateUserCode(dataSource.jsCode, {
      ...srcObj,
      ...previewVariableValues,
    })

    if (output === undefined || output === null) {
      return []
    }

    return Array.isArray(output) ? output : [output]
  }

  return results[0].records
}

interface SearchFieldsInputProps {
  value?: string[]
  onChange: (fields: string[]) => void
}

const SearchFieldsInput = ({ value, onChange }: SearchFieldsInputProps) => {
  const [inputValue, setInputValue] = useState('')
  const fields = value || []

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    const trimmed = inputValue.trim()
    if (trimmed && !fields.includes(trimmed)) {
      onChange([...fields, trimmed])
    }
    setInputValue('')
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        size='small'
        placeholder='e.g. model, reg_number'
        value={inputValue}
        onChange={event => setInputValue(event.target.value)}
        onKeyDown={handleKeyDown}
        helperText='Field names used in RSQL filter (type and press Enter)'
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {fields.map(field => (
          <Chip
            key={field}
            label={field}
            size='small'
            onDelete={() => onChange(fields.filter(item => item !== field))}
          />
        ))}
      </Box>
    </Box>
  )
}

export const MobileListItemCreateOrEdit = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = !!id

  const { listItems, createListItem, updateListItem, getListItemById } = useMobileListItems()
  const { saveDraft, clearDraft, draft, hasDraft } = useDraft<MobileListItem>(
    `mobile-list-item-draft-${id ?? 'new'}`
  )
  const shouldAutoRestore = useRef((location.state as any)?.restoreDraft === true)
  const [tabValue, setTabValue] = useState(0)
  const [jsonValue, setJsonValue] = useState('')
  const [showCommandsDialog, setShowCommandsDialog] = useState(false)
  const [modeSwitchDialog, setModeSwitchDialog] = useState<{
    open: boolean
    currentMode: 'simple' | 'advanced'
    targetMode: 'simple' | 'advanced'
  } | null>(null)

  const methods = useForm<MobileListItem>({
    defaultValues: isEdit
      ? getListItemById(id) || DEFAULT_MOBILE_LIST_ITEM
      : DEFAULT_MOBILE_LIST_ITEM,
  })

  const { control, handleSubmit, reset, watch } = methods
  const [executeCommand] = useExecuteCommandMutation()

  const normalizeIconConfig = (icon: MobileListItem['icon']): MobileListItem['icon'] => {
    if (!icon || typeof icon !== 'object') return icon

    const iconAny = icon as any
    const isJS = iconAny.isJS

    return typeof isJS === 'boolean' ? { ...iconAny, isJS } : iconAny
  }

  const normalizeListItem = (item: MobileListItem): MobileListItem =>
    normalizeListItemDisplayRules({
      ...item,
      icon: normalizeIconConfig(item.icon),
    })

  const formData = watch()
  const { isDirty } = useFormState({ control })
  const { variables: globalVariables } = useMobileGlobalVariables()
  const previewStorageKey = useMemo(
    () => formData.internalId?.trim() || id || 'new',
    [formData.internalId, id]
  )

  const listItemScriptVariables = useMemo(
    () => buildListItemScriptVariables(formData.variables || [], globalVariables),
    [formData.variables, globalVariables]
  )
  const listItemScriptFieldsInfo = useMemo(
    () => buildListItemScriptFieldsInfo(formData.variables || [], globalVariables),
    [formData.variables, globalVariables]
  )

  const [previewData, setPreviewData] = useState<any[]>([])
  const [customPreviewData, setCustomPreviewData] = useState<any[] | null>(null)
  const [previewCommandResults, setPreviewCommandResults] = useState<Record<string, any[]>>({})
  const [previewCommandMeta, setPreviewCommandMeta] = useState<PreviewCommandResult[]>([])
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewSettings, setPreviewSettings] = useState<ListItemPreviewSettings>(() =>
    loadPreviewSettings(previewStorageKey)
  )
  const [localVariablePackages, setLocalVariablePackages] = useState<PreviewVariablePackage[]>(() =>
    loadLocalVariablePackages(previewStorageKey)
  )
  const [globalVariablePackages, setGlobalVariablePackages] = useState<PreviewVariablePackage[]>(
    loadGlobalVariablePackages
  )
  const loadedPreviewStorageKeyRef = useRef(previewStorageKey)
  const skipPreviewSettingsPersistRef = useRef(false)
  const skipLocalPackagesPersistRef = useRef(false)

  // Обработчик запроса на переключение режима
  const handleModeChangeRequest = useCallback(
    (newMode: 'simple' | 'advanced') => {
      const currentMode = (formData.configMode || 'simple') as 'simple' | 'advanced'

      if (currentMode === newMode) return

      // Показать диалог подтверждения
      setModeSwitchDialog({
        open: true,
        currentMode,
        targetMode: newMode,
      })
    },
    [formData.configMode]
  )

  // Обработчик подтверждения переключения режима
  const handleModeChangeConfirm = useCallback(
    (shouldConvert: boolean) => {
      if (!modeSwitchDialog) return

      const { targetMode } = modeSwitchDialog

      if (targetMode === 'advanced') {
        // Переход из Simple в Advanced
        if (shouldConvert) {
          // Конвертировать Simple данные в Advanced
          const converted = convertSimpleToAdvanced({
            rows: formData.rows || [],
            icon: formData.icon,
          } as MobileListItem)

          methods.setValue('itemTemplate', converted || { children: [] }, { shouldDirty: true })
        } else {
          // Начать с чистого листа
          methods.setValue('itemTemplate', { children: [] }, { shouldDirty: true })
        }

        // Очистить Simple данные
        methods.setValue('rows', [], { shouldDirty: true })
        methods.setValue('icon', { value: '', isJS: false }, { shouldDirty: true })
      } else {
        // Переход из Advanced в Simple
        // Удалить Advanced данные
        methods.setValue('itemTemplate', { children: [] }, { shouldDirty: true })

        // Очистить Simple данные тоже (начинаем с чистого листа)
        methods.setValue('rows', [], { shouldDirty: true })
        methods.setValue('icon', { value: '', isJS: false }, { shouldDirty: true })
      }

      // Переключить режим
      methods.setValue('configMode', targetMode, { shouldDirty: true })

      // Закрыть диалог
      setModeSwitchDialog(null)
    },
    [modeSwitchDialog, formData.rows, formData.icon, methods]
  )

  // Обработчик отмены переключения режима
  const handleModeChangeCancel = useCallback(() => {
    setModeSwitchDialog(null)
  }, [])

  useEffect(() => {
    if (!isEdit && shouldAutoRestore.current && draft) {
      const normalizedDraft = normalizeListItem(draft.data as MobileListItem)
      Object.keys(normalizedDraft).forEach(key => {
        methods.setValue(key as any, (normalizedDraft as any)[key], {
          shouldDirty: true,
          shouldValidate: false,
        })
      })
      setJsonValue(JSON.stringify(normalizedDraft, null, 2))
      shouldAutoRestore.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEdit && id) {
      const item = getListItemById(id)
      if (item) {
        if (shouldAutoRestore.current && draft) {
          const normalizedDraft = normalizeListItem(draft.data as MobileListItem)
          Object.keys(normalizedDraft).forEach(key => {
            methods.setValue(key as any, (normalizedDraft as any)[key], {
              shouldDirty: true,
              shouldValidate: false,
            })
          })
          setJsonValue(JSON.stringify(normalizedDraft, null, 2))
          shouldAutoRestore.current = false
        } else {
          const normalized = normalizeListItem(item as MobileListItem)
          reset(normalized)
          setJsonValue(JSON.stringify(normalized, null, 2))
        }
      }
    }
  }, [id, isEdit, getListItemById, reset])

  useEffect(() => {
    if (tabValue === 2) {
      setJsonValue(JSON.stringify(formData, null, 2))
    }
  }, [formData, tabValue])

  useEffect(() => {
    skipPreviewSettingsPersistRef.current = true
    skipLocalPackagesPersistRef.current = true
    setPreviewSettings(loadPreviewSettings(previewStorageKey))
    setLocalVariablePackages(loadLocalVariablePackages(previewStorageKey))
    loadedPreviewStorageKeyRef.current = previewStorageKey
  }, [previewStorageKey])

  useEffect(() => {
    if (skipPreviewSettingsPersistRef.current) {
      skipPreviewSettingsPersistRef.current = false

      return
    }

    if (loadedPreviewStorageKeyRef.current !== previewStorageKey) {
      return
    }

    writeLocalStorageValue(
      getScopedStorageKey(PREVIEW_SETTINGS_STORAGE_PREFIX, previewStorageKey),
      previewSettings
    )
  }, [previewSettings, previewStorageKey])

  useEffect(() => {
    if (skipLocalPackagesPersistRef.current) {
      skipLocalPackagesPersistRef.current = false

      return
    }

    if (loadedPreviewStorageKeyRef.current !== previewStorageKey) {
      return
    }

    writeLocalStorageValue(
      getScopedStorageKey(LOCAL_VARIABLE_PACKAGES_STORAGE_PREFIX, previewStorageKey),
      localVariablePackages
    )
  }, [localVariablePackages, previewStorageKey])

  useEffect(() => {
    writeLocalStorageValue(GLOBAL_VARIABLE_PACKAGES_STORAGE_KEY, globalVariablePackages)
  }, [globalVariablePackages])

  const [errors, setErrors] = useState<string[]>([])
  const [saveError, setSaveError] = useState<string | null>(null)

  const onSubmit = async (data: MobileListItem) => {
    const validationErrors = validateMobileListItem(data)

    if (!isEdit) {
      const duplicate = listItems.find(
        (item: MobileListItem) => item.internalId === data.internalId?.trim()
      )
      if (duplicate) {
        validationErrors.push('A config with this Internal ID already exists')
      }
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors)

      return
    }

    setErrors([])

    // Ensure itemType is always 'dictionary'
    const itemData = {
      ...data,
      itemType: 'dictionary',
    }
    const normalizedItemData = normalizeListItem(itemData as MobileListItem)

    try {
      if (isEdit && id) {
        const currentItem = getListItemById(id)
        if (currentItem) {
          const updatedItem: MobileListItem = {
            ...normalizedItemData,
            id: currentItem.id,
          }
          await updateListItem(id, normalizedItemData)
          reset(updatedItem)
          setJsonValue(JSON.stringify(updatedItem, null, 2))
          clearDraft()
        } else {
          await updateListItem(id, normalizedItemData)
          clearDraft()
        }
      } else {
        const newItem = await createListItem(normalizedItemData)
        clearDraft()
        navigate(`${ROUTES.CONFIG_MOBILE_LIST_ITEMS_EDIT}/${newItem.id}`, { replace: true })
      }
    } catch (error: unknown) {
      setSaveError(extractServerErrorMessage(error))
    }
  }

  const handleCancel = () => {
    navigate(ROUTES.CONFIG_MOBILE_LIST_ITEMS)
  }

  const handleReset = () => {
    if (isEdit && id) {
      const item = getListItemById(id)
      if (item) {
        const normalized = normalizeListItem(item)
        methods.reset(normalized)
        setJsonValue(JSON.stringify(normalized, null, 2))
        setErrors([])
      }
    } else {
      methods.reset(DEFAULT_MOBILE_LIST_ITEM)
      setJsonValue(JSON.stringify(DEFAULT_MOBILE_LIST_ITEM, null, 2))
      setErrors([])
    }
  }

  const handleJsonChange = (newValue: string) => {
    try {
      const parsed = JSON.parse(newValue)
      // Use setValue for each field to mark form as dirty
      Object.keys(parsed).forEach(key => {
        methods.setValue(key as any, parsed[key], { shouldDirty: true, shouldValidate: false })
      })
      setJsonValue(newValue)
    } catch (error) {
      // Invalid JSON, just update the state
      setJsonValue(newValue)
    }
  }

  const handleOpenCommandsDialog = () => {
    setShowCommandsDialog(true)
  }

  const handleCloseCommandsDialog = () => {
    setShowCommandsDialog(false)
  }

  const handleChangeCommands = (commands?: GETCommand[] | null) => {
    methods.setValue('getDataCommands', commands, { shouldDirty: true })
  }

  const watchCommands = methods.watch('getDataCommands')

  const previewCommandResultsByObject = useMemo(() => {
    return previewCommandMeta.reduce((acc, entry) => {
      if (entry.command.objectCode) {
        acc[entry.command.objectCode] = entry.records
      }

      return acc
    }, {} as Record<string, any[]>)
  }, [previewCommandMeta])

  const previewVariableValues = useMemo<PreviewVariableValues>(() => {
    const selectedLocalPackage = localVariablePackages.find(
      pkg => pkg.id === previewSettings.selectedLocalVariablePackageId
    )
    const selectedGlobalPackage = globalVariablePackages.find(
      pkg => pkg.id === previewSettings.selectedGlobalVariablePackageId
    )

    return {
      localVariables: {
        ...buildVariableDefaultValues(formData.variables || []),
        ...(selectedLocalPackage?.values ?? {}),
      },
      globalVariables: {
        ...buildVariableDefaultValues(globalVariables || []),
        ...(selectedGlobalPackage?.values ?? {}),
      },
    }
  }, [
    formData.variables,
    globalVariables,
    globalVariablePackages,
    localVariablePackages,
    previewSettings.selectedGlobalVariablePackageId,
    previewSettings.selectedLocalVariablePackageId,
  ])

  const handleRefreshPreview = useCallback(async () => {
    const currentValues = methods.getValues()
    const commands = currentValues.getDataCommands ?? []

    if (!commands.length) {
      setPreviewError('Добавьте хотя бы одну команду для предварительного просмотра')

      return
    }

    // Reset custom data when refreshing from API
    setCustomPreviewData(null)
    setPreviewLoading(true)
    setPreviewError(null)

    const settled = await Promise.allSettled(
      commands.map(async (command, index) => {
        const key = getCommandKey(index)
        const commandLabel = command.name || key

        if (!command.name || !command.objectCode) {
          throw new Error(
            `Команда "${commandLabel}" не содержит необходимые параметры (name или objectCode)`
          )
        }

        const params = getCommandRequestData(
          getPreviewCommandParameters(command.parameters, previewSettings.skipFilterGetData),
          {},
          undefined,
          undefined,
          previewVariableValues
        )
        const body = getCommandRequestData(
          getPreviewCommandParameters(command.body, previewSettings.skipFilterGetData),
          {},
          undefined,
          undefined,
          previewVariableValues
        )

        try {
          const response = await executeCommand({
            commandCode: command.name,
            objectCode: command.objectCode,
            method: command.httpMethod || 'POST',
            params,
            body,
          }).unwrap()

          const records = normalizeCommandRecords(response)

          return {
            key,
            command,
            rawData: response,
            records,
          } as PreviewCommandResult
        } catch (error) {
          throw new Error(
            `Ошибка при выполнении команды "${commandLabel}": ${extractErrorMessage(error)}`
          )
        }
      })
    )

    const rejectedResult = settled.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    )

    if (rejectedResult) {
      setPreviewError(extractErrorMessage(rejectedResult.reason))
      setPreviewLoading(false)

      return
    }

    const fulfilledResults = settled as PromiseFulfilledResult<PreviewCommandResult>[]
    const results = fulfilledResults.map(result => result.value)

    const srcObjMap = results.reduce((acc, entry) => {
      acc[entry.key] = entry.records

      if (entry.command.objectCode) {
        acc[entry.command.objectCode] = entry.records
      }

      return acc
    }, {} as Record<string, any[]>)

    try {
      const primaryItems = derivePreviewItems(
        currentValues as MobileListItem,
        results,
        previewVariableValues
      )

      setPreviewCommandResults(srcObjMap)
      setPreviewCommandMeta(results)
      setPreviewData(primaryItems)
      setPreviewError(null)
    } catch (error) {
      setPreviewError(extractErrorMessage(error))
    } finally {
      setPreviewLoading(false)
    }
  }, [executeCommand, methods, previewSettings.skipFilterGetData, previewVariableValues])

  useEffect(() => {
    if (tabValue === 1) {
      handleRefreshPreview()
    }
  }, [tabValue, handleRefreshPreview])

  const handleDataEdit = useCallback((newData: any[]) => {
    setCustomPreviewData(newData)
  }, [])

  // Preview для Config вкладки
  const previewComponent = useMemo(() => {
    if (tabValue === 1) {
      return (
        <MobileListItemPreview
          item={formData}
          data={customPreviewData ?? previewData}
          onRefresh={handleRefreshPreview}
          onDataEdit={handleDataEdit}
          isLoading={previewLoading}
          error={previewError}
          commandResultsByKey={previewCommandResults}
          commandResultsByObject={previewCommandResultsByObject}
          previewSettings={previewSettings}
          onPreviewSettingsChange={setPreviewSettings}
          localVariables={formData.variables || []}
          globalVariables={globalVariables || []}
          localVariablePackages={localVariablePackages}
          globalVariablePackages={globalVariablePackages}
          onLocalVariablePackagesChange={setLocalVariablePackages}
          onGlobalVariablePackagesChange={setGlobalVariablePackages}
          previewVariableValues={previewVariableValues}
        />
      )
    }

    return undefined
  }, [
    tabValue,
    formData,
    previewData,
    customPreviewData,
    handleRefreshPreview,
    handleDataEdit,
    previewLoading,
    previewError,
    previewCommandResults,
    previewCommandResultsByObject,
    previewSettings,
    formData.variables,
    globalVariables,
    localVariablePackages,
    globalVariablePackages,
    previewVariableValues,
  ])

  return (
    <>
      <SaveDraftDialog
        open={!!saveError}
        errorMessage={saveError ?? ''}
        onSaveDraft={() => saveDraft(methods.getValues())}
        onClose={() => setSaveError(null)}
      />
      <MobileConfiguratorLayout
        title={isEdit ? 'Edit List Item Config' : 'Create List Item Config'}
        isEdit={isEdit}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        onReset={handleReset}
        isDirty={isDirty}
        errors={errors}
        previewComponent={previewComponent}
        draftProps={{
          hasDraft,
          draftSavedAt: draft?.savedAt,
          onRestoreDraft: () => {
            if (draft) {
              const normalized = normalizeListItem(draft.data as MobileListItem)
              methods.reset(normalized, { keepDefaultValues: true })
              setJsonValue(JSON.stringify(normalized, null, 2))
            }
          },
          onDiscardDraft: clearDraft,
          onSaveDraft: () => saveDraft(methods.getValues()),
        }}
      >
        <Paper sx={{ width: '100%', mb: 2 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label='Basic Info' />
            <Tab label='Config' />
            <Tab label='Variables' />
            <Tab label='Actions' />
            <Tab label='JSON Editor' />
          </Tabs>
        </Paper>

        <FormProvider {...methods}>
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Controller
                  name='internalId'
                  control={control}
                  rules={{
                    required: 'Internal ID is required',
                    validate: value => {
                      if (!value?.trim() || isEdit) return true
                      const duplicate = listItems.find(
                        (item: MobileListItem) => item.internalId === value.trim()
                      )

                      return duplicate ? 'A config with this Internal ID already exists' : true
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <InternalIdInput
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      fullWidth
                      label='Internal ID'
                      error={!!error}
                      helperText={error?.message}
                      required
                      disabled={isEdit}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name='title'
                  control={control}
                  rules={{ required: 'Title is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label='Config Title'
                      error={!!error}
                      helperText={error?.message}
                      required
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant='subtitle2'>Get Data Commands</Typography>
                    <Typography variant='body2' color='textSecondary'>
                      {watchCommands?.length || 0} command(s)
                    </Typography>
                  </Box>
                  <Button variant='outlined' fullWidth onClick={handleOpenCommandsDialog}>
                    {watchCommands?.length ? 'Edit Commands' : 'Add Commands'}
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <DataSourceConfig getDataCommands={formData.getDataCommands || null} />
              </Grid>

              {formData.dataSource?.bindType === 'prefilled' && (
                <Grid item xs={12}>
                  <PrefilledVariablesConfig />
                </Grid>
              )}

              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant='subtitle1' gutterBottom>
                    Search Configuration
                  </Typography>
                  <Controller
                    name='enableSearch'
                    control={control}
                    render={({ field }) => (
                      <Tooltip title='Show a search bar above the list. Requires at least one command data source and searchFields.'>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!field.value}
                              onChange={e => field.onChange(e.target.checked)}
                            />
                          }
                          label='Enable Search'
                        />
                      </Tooltip>
                    )}
                  />
                  {formData.enableSearch && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant='body2' color='text.secondary' gutterBottom>
                        Search Fields (press Enter to add)
                      </Typography>
                      <Controller
                        name='searchFields'
                        control={control}
                        defaultValue={[]}
                        render={({ field }) => (
                          <SearchFieldsInput value={field.value || []} onChange={field.onChange} />
                        )}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>

              {showCommandsDialog && (
                <AddCommandsDialog
                  objectCode=''
                  commands={watchCommands}
                  onCloseDialog={handleCloseCommandsDialog}
                  onChangeCommands={handleChangeCommands}
                  commandPickerControllerProps={{
                    isTypeHidden: true,
                  }}
                  scriptVariables={listItemScriptVariables}
                  scriptFieldsInfo={listItemScriptFieldsInfo}
                />
              )}
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='subtitle1'>Configuration Mode</Typography>
                  <Controller
                    name='configMode'
                    control={control}
                    defaultValue='simple'
                    render={({ field }) => (
                      <ToggleButtonGroup
                        value={field.value || 'simple'}
                        exclusive
                        onChange={(_, value) => {
                          if (value !== null) {
                            handleModeChangeRequest(value as 'simple' | 'advanced')
                          }
                        }}
                        size='small'
                      >
                        <ToggleButton value='simple'>Simple</ToggleButton>
                        <ToggleButton value='advanced'>Advanced</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                </Box>
              </Paper>

              {formData.configMode === 'advanced' ? (
                <AdvancedConfigTab />
              ) : (
                <>
                  <Paper sx={{ p: 2 }}>
                    <IconConfigSection />
                  </Paper>
                  <Paper sx={{ p: 2 }}>
                    <FabConfigSection />
                  </Paper>
                  <RowsConfigTab />
                </>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <VariablesConfigTab />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <ActionsConfigTab />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <JsonEditor
              value={jsonValue}
              onChange={handleJsonChange}
              label='List Item Configuration (JSON)'
              minHeight={400}
            />
          </TabPanel>
        </FormProvider>

        {/* Config Mode Switch Dialog */}
        {modeSwitchDialog && (
          <ConfigModeSwitchDialog
            open={modeSwitchDialog.open}
            currentMode={modeSwitchDialog.currentMode}
            targetMode={modeSwitchDialog.targetMode}
            onConfirm={handleModeChangeConfirm}
            onCancel={handleModeChangeCancel}
          />
        )}
      </MobileConfiguratorLayout>
    </>
  )
}
